import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
import {useGetTraceServerClientContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import {type Query} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientInterface/query';
import {
  type AgentSpanStatsAggregation,
  type AgentSpanStatsMetricSpec,
  type AgentSpanValueRef,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';
import {useEffect, useMemo, useState} from 'react';

import {
  SPAN_CUSTOM_ATTR_SOURCES,
  SPAN_DERIVED_METRIC_ALIASES,
  SPAN_ERROR_METRIC_PATHS,
  SPAN_FIELD_METRIC_PATHS,
  SPAN_IS_ERROR_VALUE_REF,
} from '../constants';
import {
  type AggregationFunction,
  type WeaveMetricThresholdConfig,
} from '../types';

const SPAN_METRIC_ALIAS = 'metric';

// One time bucket: the derived metric value, plus the matched (e.g. error) and
// total counts behind it (the full population that entered the window). Counts
// are null for numeric metrics, which have no match/total split.
export type SpanAlertBucket = {
  timestamp: number;
  value: number | null;
  matchedCount: number | null;
  totalCount: number | null;
};

export const isSpanErrorRate = (metricPath: string): boolean =>
  SPAN_ERROR_METRIC_PATHS.includes(metricPath);

/**
 * Spans-source data for the metric charts. The agent-spans stats endpoint
 * buckets and aggregates server-side, so each returned row maps straight to a
 * bucket. Error metrics request count_true (matched) + count (total) so both
 * the derived value and the population it came from are available.
 */
export const useSpanAlertChartData = (params: {
  projectId: string;
  metricFilter: WeaveMetricThresholdConfig['metricFilter'];
  startMs: number;
  endMs: number;
  granularitySeconds: number;
  enabled: boolean;
}): {
  buckets: SpanAlertBucket[];
  loading: boolean;
  error: Error | null;
  hasData: boolean;
} => {
  const {projectId, metricFilter, startMs, endMs, granularitySeconds, enabled} =
    params;
  const getTsClient = useGetTraceServerClientContext();
  const [buckets, setBuckets] = useState<SpanAlertBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const metricPath = metricFilter.metricPath ?? '';
  const aggFn = metricFilter.aggregationFunction ?? 'mean';
  const {spanOperationName, spanName, spanAgentName} = metricFilter;
  const query = useMemo(
    () => buildSpanFilterQuery(spanOperationName, spanName, spanAgentName),
    [spanOperationName, spanName, spanAgentName]
  );

  useEffect(() => {
    if (!enabled || !metricPath) {
      setBuckets([]);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const run = async (): Promise<void> => {
      try {
        const res = await getTsClient().agentSpansStats({
          project_id: projectId,
          start: new Date(startMs).toISOString(),
          end: new Date(endMs).toISOString(),
          granularity: granularitySeconds,
          query,
          metrics: [buildSpanMetricSpec(metricPath, aggFn)],
        });
        if (!active) {
          return;
        }
        setBuckets(
          res.rows
            .flatMap(row => {
              const bucket = spanRowToBucket(row, metricPath, aggFn);
              return bucket ? [bucket] : [];
            })
            .sort((a, b) => a.timestamp - b.timestamp)
        );
      } catch (e) {
        if (active) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setBuckets([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [
    enabled,
    getTsClient,
    projectId,
    metricPath,
    aggFn,
    query,
    startMs,
    endMs,
    granularitySeconds,
  ]);

  const hasData = buckets.some(b => b.value !== null);
  return {buckets, loading, error, hasData};
};

// Map one stats row to a bucket. The trace server sends the timestamp as a
// Z-less UTC string, so parse via the canonical helper (a bare new Date would
// shift it by the local offset). Non-string timestamps are dropped.
export const spanRowToBucket = (
  row: Record<string, string | number | boolean | null>,
  metricPath: string,
  aggFn: AggregationFunction
): SpanAlertBucket | null => {
  const ts = row.timestamp;
  if (typeof ts !== 'string') {
    return null;
  }
  return {
    timestamp: unixTimestampMSFromUTCString(ts),
    ...extractBucket(row, metricPath, aggFn),
  };
};

// Build the agent-spans stats metric spec. Error rate uses the derived boolean
// is_error (count_true/count); numeric metrics use avg/min/max. Mirrors
// _resolve_span_metric in alert_worker/spans_metric_source.py.
const buildSpanMetricSpec = (
  metricPath: string,
  aggFn: AggregationFunction
): AgentSpanStatsMetricSpec => {
  if (isSpanErrorRate(metricPath)) {
    return {
      alias: SPAN_METRIC_ALIAS,
      value_type: 'boolean',
      value: SPAN_IS_ERROR_VALUE_REF,
      aggregations: ['count_true', 'count'],
    };
  }
  return {
    alias: SPAN_METRIC_ALIAS,
    value_type: 'number',
    value: toSpanValueRef(metricPath),
    aggregations: [toSpanAggregation(aggFn)],
  };
};

// Reduce one bucket row to its metric value and the matched/total counts. Error
// rate: an empty bucket (no spans) is a null-value gap; otherwise percentage is
// 100*count_true/count and count is the raw error count. Numeric: the single
// <agg> column, with no match/total split.
const extractBucket = (
  row: Record<string, string | number | boolean | null>,
  metricPath: string,
  aggFn: AggregationFunction
): Pick<SpanAlertBucket, 'value' | 'matchedCount' | 'totalCount'> => {
  if (isSpanErrorRate(metricPath)) {
    const total = numericCell(row[`count_${SPAN_METRIC_ALIAS}`]);
    if (total === null || total === 0) {
      return {value: null, matchedCount: 0, totalCount: total ?? 0};
    }
    const errors = numericCell(row[`count_true_${SPAN_METRIC_ALIAS}`]) ?? 0;
    const value = aggFn === 'count' ? errors : (100 * errors) / total;
    return {value, matchedCount: errors, totalCount: total};
  }
  return {
    value: numericCell(row[`${toSpanAggregation(aggFn)}_${SPAN_METRIC_ALIAS}`]),
    matchedCount: null,
    totalCount: null,
  };
};

const numericCell = (
  raw: string | number | boolean | null | undefined
): number | null =>
  typeof raw === 'number' && Number.isFinite(raw) ? raw : null;

// Resolve a span metric_path to a value ref the same way the alert worker does
// (custom-attr prefixes, token-subtype fields, derived aliases like latency_ms
// and cost), so the preview chart matches the alert that actually fires.
const toSpanValueRef = (metricPath: string): AgentSpanValueRef => {
  const custom = SPAN_CUSTOM_ATTR_SOURCES.find(({prefix}) =>
    metricPath.startsWith(prefix)
  );
  if (custom) {
    return {source: custom.source, key: metricPath.slice(custom.prefix.length)};
  }
  if (SPAN_FIELD_METRIC_PATHS.includes(metricPath)) {
    return {source: 'field', key: metricPath};
  }
  return {
    source: 'derived',
    key: SPAN_DERIVED_METRIC_ALIASES[metricPath] ?? metricPath,
  };
};

// The spans builder restricts numeric aggregation to mean/min/max/sum; mean
// maps to avg. Other AggregationFunction values (median/mode/percentage/count)
// are calls-only or error-rate-only and never reach a numeric span metric.
const toSpanAggregation = (
  fn: AggregationFunction
): AgentSpanStatsAggregation => {
  if (fn === 'mean') {
    return 'avg';
  }
  if (fn === 'min') {
    return 'min';
  }
  if (fn === 'max') {
    return 'max';
  }
  if (fn === 'sum') {
    return 'sum';
  }
  throw new Error(`unsupported span aggregation function: ${fn}`);
};

const buildSpanFilterQuery = (
  operationName: string | undefined,
  spanName: string | undefined,
  agentName: string | undefined
): Query | undefined => {
  type Expr = Query['$expr'];
  const eq = (field: string, value: string): Expr => ({
    $eq: [{$getField: field}, {$literal: value}],
  });
  const exprs: Expr[] = [];
  if (operationName) {
    exprs.push(eq('operation_name', operationName));
  }
  if (spanName) {
    exprs.push(eq('span_name', spanName));
  }
  if (agentName) {
    exprs.push(eq('agent_name', agentName));
  }
  if (exprs.length === 0) {
    return undefined;
  }
  return {$expr: exprs.length === 1 ? exprs[0] : {$and: exprs}};
};
